import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [period, setPeriod] = useState('month');
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    const mockPayments = [
      { id: 1, orderNumber: 'AQ-2024001', amount: 45.90, status: 'completed', method: 'Carte bancaire', date: '2024-06-14 14:30', customer: 'Sophie Martin', transactionId: 'tr_123456' },
      { id: 2, orderNumber: 'AQ-2024002', amount: 32.40, status: 'pending', method: 'Paiement à la livraison', date: '2024-06-14 11:15', customer: 'Thomas Bernard', transactionId: null },
      { id: 3, orderNumber: 'AQ-2024003', amount: 67.80, status: 'completed', method: 'PayPal', date: '2024-06-13 16:45', customer: 'Isabelle Dubois', transactionId: 'tr_789012' },
      { id: 4, orderNumber: 'AQ-2024004', amount: 23.50, status: 'completed', method: 'Carte bancaire', date: '2024-06-13 09:20', customer: 'Pierre Richard', transactionId: 'tr_345678' },
      { id: 5, orderNumber: 'AQ-2024005', amount: 54.20, status: 'failed', method: 'Carte bancaire', date: '2024-06-12 13:00', customer: 'Marie Lambert', transactionId: 'tr_901234', error: 'Carte expirée' },
      { id: 6, orderNumber: 'AQ-2024006', amount: 89.90, status: 'completed', method: 'Carte bancaire', date: '2024-06-11 10:30', customer: 'Nicolas Petit', transactionId: 'tr_567890' },
      { id: 7, orderNumber: 'AQ-2024007', amount: 12.99, status: 'refunded', method: 'Carte bancaire', date: '2024-06-10 15:20', customer: 'Julie Robert', transactionId: 'tr_123789', refundReason: 'Produit indisponible' }
    ];
    setPayments(mockPayments);
    setLoading(false);
  }, []);

  const stats = {
    total: payments.reduce((sum, p) => sum + (p.status === 'completed' ? p.amount : 0), 0),
    count: payments.filter(p => p.status === 'completed').length,
    pending: payments.filter(p => p.status === 'pending').length,
    failed: payments.filter(p => p.status === 'failed').length,
    refunded: payments.filter(p => p.status === 'refunded').length
  };

  const methodStats = {
    'Carte bancaire': payments.filter(p => p.method === 'Carte bancaire' && p.status === 'completed').reduce((sum, p) => sum + p.amount, 0),
    'PayPal': payments.filter(p => p.method === 'PayPal' && p.status === 'completed').reduce((sum, p) => sum + p.amount, 0),
    'Paiement à la livraison': payments.filter(p => p.method === 'Paiement à la livraison' && p.status === 'completed').reduce((sum, p) => sum + p.amount, 0)
  };

  const dailyData = [
    { date: '10/06', montant: 12.99, nb: 1 },
    { date: '11/06', montant: 89.90, nb: 1 },
    { date: '12/06', montant: 0, nb: 0 },
    { date: '13/06', montant: 91.30, nb: 2 },
    { date: '14/06', montant: 78.30, nb: 2 }
  ];

  const pieData = [
    { name: 'Carte bancaire', value: methodStats['Carte bancaire'], color: '#0B1A33' },
    { name: 'PayPal', value: methodStats['PayPal'], color: '#D4AF37' },
    { name: 'Paiement à la livraison', value: methodStats['Paiement à la livraison'], color: '#2D2D2D' }
  ];

  const getStatusBadge = (status) => {
    const badges = {
      completed: { label: 'Réussi', color: 'bg-green-100 text-green-600' },
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-600' },
      failed: { label: 'Échoué', color: 'bg-red-100 text-red-600' },
      refunded: { label: 'Remboursé', color: 'bg-orange-100 text-orange-600' }
    };
    const b = badges[status];
    return <span className={`px-2 py-1 rounded-full text-xs ${b?.color}`}>{b?.label}</span>;
  };

  const getMethodIcon = (method) => {
    if (method === 'Carte bancaire') return '💳';
    if (method === 'PayPal') return '📧';
    return '🚚';
  };

  const filteredPayments = payments.filter(p => {
    const matchFilter = filter === 'all' || p.status === filter;
    const matchSearch = p.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        p.customer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchFilter && matchSearch;
  });

  const exportToExcel = () => {
    // Simulation export Excel
    alert('Export Excel en cours...');
  };

  const exportToPDF = () => {
    // Simulation export PDF
    alert('Export PDF en cours...');
  };

  if (loading) return <div className="text-center py-20">Chargement...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Gestion des paiements</h1>
          <p className="text-gray-500 mt-1">Suivez et gérez tous les paiements</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportToExcel} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Export Excel
          </button>
          <button onClick={exportToPDF} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Export PDF
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-md p-4 text-center">
          <p className="text-2xl font-bold text-primary">{stats.total.toFixed(2)} €</p>
          <p className="text-xs text-gray-500">Chiffre d'affaires</p>
          <p className="text-xs text-gray-400">{stats.count} transactions</p>
        </div>
        <div className="bg-yellow-50 rounded-xl shadow-md p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          <p className="text-xs">En attente</p>
        </div>
        <div className="bg-red-50 rounded-xl shadow-md p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
          <p className="text-xs">Échoués</p>
        </div>
        <div className="bg-orange-50 rounded-xl shadow-md p-4 text-center">
          <p className="text-2xl font-bold text-orange-600">{stats.refunded}</p>
          <p className="text-xs">Remboursés</p>
        </div>
        <div className="bg-green-50 rounded-xl shadow-md p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{((stats.total / 50000) * 100).toFixed(0)}%</p>
          <p className="text-xs">Objectif mensuel</p>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-md p-5">
          <h2 className="font-bold text-lg mb-4">📊 Évolution des paiements</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="montant" fill="#D4AF37" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5">
          <h2 className="font-bold text-lg mb-4">🥧 Répartition par moyen de paiement</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" label>
                {pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            {pieData.map((item, idx) => (<div key={idx} className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div><span className="text-sm">{item.name}</span><span className="text-sm font-bold">{item.value.toFixed(2)} €</span></div>))}
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-2">
            <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-lg transition ${filter === 'all' ? 'bg-primary text-white' : 'bg-gray-100'}`}>Tous</button>
            <button onClick={() => setFilter('completed')} className={`px-4 py-2 rounded-lg transition ${filter === 'completed' ? 'bg-primary text-white' : 'bg-gray-100'}`}>Réussis</button>
            <button onClick={() => setFilter('pending')} className={`px-4 py-2 rounded-lg transition ${filter === 'pending' ? 'bg-primary text-white' : 'bg-gray-100'}`}>En attente</button>
            <button onClick={() => setFilter('failed')} className={`px-4 py-2 rounded-lg transition ${filter === 'failed' ? 'bg-primary text-white' : 'bg-gray-100'}`}>Échoués</button>
            <button onClick={() => setFilter('refunded')} className={`px-4 py-2 rounded-lg transition ${filter === 'refunded' ? 'bg-primary text-white' : 'bg-gray-100'}`}>Remboursés</button>
          </div>
          <div className="relative">
            <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border rounded-lg w-64" />
            <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </div>
        </div>
      </div>

      {/* Tableau des paiements */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Transaction</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Client</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Méthode</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Montant</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map(payment => (
                <tr key={payment.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-mono text-sm font-medium">{payment.orderNumber}</p>
                      {payment.transactionId && <p className="text-xs text-gray-400">{payment.transactionId}</p>}
                    </div>
                  </td>
                  <td className="px-4 py-3">{payment.customer}</td>
                  <td className="px-4 py-3 text-sm">{payment.date}</td>
                  <td className="px-4 py-3"><span className="flex items-center gap-1">{getMethodIcon(payment.method)} {payment.method}</span></td>
                  <td className="px-4 py-3 font-bold">{payment.amount.toFixed(2)} €</td>
                  <td className="px-4 py-3">{getStatusBadge(payment.status)}</td>
                  <td className="px-4 py-3"><button onClick={() => setSelectedPayment(payment)} className="text-secondary hover:underline text-sm">Vérifier</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal vérification transaction */}
      {selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-bold">Transaction #{selectedPayment.orderNumber}</h2><button onClick={() => setSelectedPayment(null)} className="text-gray-500 text-2xl">✕</button></div>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg"><p className="font-semibold">Client</p><p>{selectedPayment.customer}</p></div>
              <div className="p-3 bg-gray-50 rounded-lg"><p className="font-semibold">Montant</p><p className="text-2xl font-bold text-primary">{selectedPayment.amount.toFixed(2)} €</p></div>
              <div className="p-3 bg-gray-50 rounded-lg"><p className="font-semibold">Méthode</p><p>{getMethodIcon(selectedPayment.method)} {selectedPayment.method}</p></div>
              <div className="p-3 bg-gray-50 rounded-lg"><p className="font-semibold">Statut</p>{getStatusBadge(selectedPayment.status)}</div>
              {selectedPayment.error && <div className="p-3 bg-red-50 rounded-lg"><p className="font-semibold text-red-600">Erreur</p><p className="text-sm">{selectedPayment.error}</p></div>}
              {selectedPayment.refundReason && <div className="p-3 bg-orange-50 rounded-lg"><p className="font-semibold text-orange-600">Motif remboursement</p><p className="text-sm">{selectedPayment.refundReason}</p></div>}
            </div>
            <div className="flex gap-3 mt-4"><button className="flex-1 bg-primary text-white py-2 rounded-lg">Valider</button><button onClick={() => setSelectedPayment(null)} className="flex-1 bg-gray-200 py-2 rounded-lg">Fermer</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;